import { Component, OnDestroy, OnInit } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { TemperatureReadingComponent } from "@app/directives/_misc/temperatureReading/temperatureReading.component";
import { ProgressColourPalette, VProgressBar } from "@app/directives/_misc/v-progress-bar/v-progress-bar.component";
import { SortType } from "@app/enum/SortType";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { LatestSensorDataValues } from "@interfaces/ISensorDataValue";
import { IVehicle } from "@interfaces/IVehicle";
import { DateService, DateUnit } from "@services/date.service";
import { WindowBreakpoint } from "@services/responsive.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { TemperatureNavigationModule } from "@temperature/temperature.navigation";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferEngineModeCategory } from "@temperature/_enums/reefer/ReeferEngineModeCategory.enum";
import { ReeferOperatingModeCategory } from "@temperature/_enums/reefer/ReeferOperatingModeCategory.enum";
import { TemperatureProbeCategory } from "@temperature/_enums/temperature/TemperatureProbeCategory.enum";
import { IMultiStateInput } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IMultiStateInputRecord } from "@temperature/_models/multiStateInputs/IMultiStateInputRecord";
import { IReefer } from "@temperature/_models/reefer/IReefer";
import { IReeferWithMappings } from "@temperature/_models/reefer/IReeferWithMappings";
import { ITemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureOutOfBoundsHistory";
import { ITemperatureProbe, ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { ITemperatureSetpoint } from "@temperature/_models/temperature/ITemperatureSetpoint";
import { ITemperatureSetpointHistory } from "@temperature/_models/temperature/ITemperatureSetpointHistory";
import { IZone, IZoneWithProbes } from "@temperature/_models/temperature/ITemperatureZone";
import { IZoneTemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureZoneOutOfBoundsHistory";
import { MultiStateInputService } from "@temperature/_services/multi-state-input.service";
import { ReeferService } from "@temperature/_services/reefer.service";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import { TemperatureSetpointService } from "@temperature/_services/temperature-setpoint.service";
import { ZoneService } from "@temperature/_services/zone.service";
import { lastValueFrom } from "rxjs";
import { ZoneOperatingModeWidget } from "../zone/zoneOperatingModeWidget/zone-operating-mode-widget.component";
import { UnitConversionService, UnitConversionType } from "../../../../../services/unitConversion.service";
import './reefer-list.component.scss';
import { IDiagnosticRecord } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticRecord";
import { IDiagnosticTroubleCode } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticTroubleCode";
import { IVehicleWithSensorDataValues } from "@app/main/modules/maintenance/_components/sensorData/_models/IVehicleWithSensorData";
import { SensorDataService } from "@app/main/modules/maintenance/_components/sensorData/sensor-data.service";
import { FaultCodesService } from "@app/main/modules/maintenance/_components/faultCodes/fault-codes.service";

@Component({
    selector: 'reefer-list',
    template: require('./reefer-list.component.html')
})
export class ReeferListComponent implements OnInit, OnDestroy {
    filterEvent: IAggregateFilterEvent<IReeferWithMappings>;

    reeferData: IReeferWithMappings[];
    filteredData: IReeferWithMappings[];
    vehicles: IVehicle[];

    zones: IZoneWithProbes[];

    latestMultiStateInputRecords: IMultiStateInputRecord[];
    multiStateInputs: IMultiStateInput[];
    temperatureSetpoints: ITemperatureSetpoint[];
    temperatureSetpointHistories: ITemperatureSetpointHistory[];
    zoneTemperatureOutOfBoundsHistory: IZoneTemperatureOutOfBoundsHistory[];

    temperatureProbes: ITemperatureProbeWithTemperatureData[];
    temperatureRecords: ITemperatureRecord[];

    diagnosticRecords: IDiagnosticRecord[];
    sensorDataValues: LatestSensorDataValues[];
    diagnosticTroubleCodes: IDiagnosticTroubleCode[];

    filterTabs: IFilterBarTab<IReeferWithMappings>[] = [];

    loading: boolean;
    updateInterval: ReturnType<typeof setInterval>;

    WindowBreakpoint = WindowBreakpoint;
    ReeferOperatingModeCategory = ReeferOperatingModeCategory;
    ReeferEngineModeCategory = ReeferEngineModeCategory;
    TemperatureProbeCategory = TemperatureProbeCategory;

    dateRangeStart: Date;

    tableColumns: IVTableColumn<IReeferWithMappings>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly faultCodeService: FaultCodesService,
        private readonly multiStateInputService: MultiStateInputService,
        private readonly reeferService: ReeferService,
        private readonly sensorDataService: SensorDataService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureSetpointService: TemperatureSetpointService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly vTableService: VTableService,
        private readonly zoneService: ZoneService,
        private readonly dateService: DateService,
        private readonly conversionService: UnitConversionService
    ) {
        this.dateRangeStart = this.dateService.addOrSubtractTime(new Date, -1, DateUnit.Day)
    }

    ngOnInit() {
        this.createTabs();

        this.createTable();
        this.getData(true);

        this.updateInterval = setInterval(() => {
            this.getData();
        }, 60_000);
    }

    ngOnDestroy() {
        clearInterval(this.updateInterval);
    }

    onRowClicked($event) {
        this.reeferService.openReeferDetailsDialog($event.data.vehicle.Id, TemperatureNavigationModule.Reefer);
    }

    onFilterChanged($event) {
        this.filterEvent = $event;
    }

    //--

    private getData(first = false) {
        if (first) {
            this.loading = true;
        }

        const propertiesPromise = this.getPropertiesForReeferData();
        const reeferPromise: Promise<IReefer[]> = lastValueFrom(this.reeferService.getReeferData())

        Promise.all([reeferPromise, propertiesPromise]).then(([reeferUnits, _]) => {
            this.reeferData = reeferUnits;
            this.reeferData.forEach((reefer) => {
                if (!this.vehicles.find(vehicle => vehicle.Id === reefer.VehicleId)) {
                    // User does not have access to this vehicle or their preferences are to hide it.
                    return;
                }

                this.setupSensorDataForReefer(reefer);

                this.getModes(reefer);

                this.temperatureSetpointService.mapHistoriesToSetPoints(this.temperatureSetpoints, this.temperatureSetpointHistories);

                reefer.multiStateInputRecords = this.multiStateInputService.getVehicleMultiStateInputHistory(reefer.VehicleId, this.latestMultiStateInputRecords);
                this.mapPropertiesToZones(reefer);

                const diagnosticRecords = this.diagnosticRecords.filter(diagnosticRecord => diagnosticRecord.VehicleId === reefer.VehicleId);

                reefer.lastUpdated = this.reeferService.getLastUpdated(reefer.zones, diagnosticRecords);
                reefer.activeErrors = this.reeferService.getActiveReeferErrors(diagnosticRecords, this.diagnosticTroubleCodes);
                reefer.currentError = this.getActiveReeferError(reefer).error;
                reefer.errorStatus = this.getActiveReeferError(reefer).status;
                reefer.plotId = reefer.vehicle?.PlotId;
            });
        }).finally(() => {
            this.filteredData = this.reeferData.filter(reefer => !!reefer.lastUpdated);
            this.tableRows = this.vTableService.createTableRows(this.filteredData, this.tableColumns);
            this.loading = false;
        });
    }

    private getPropertiesForReeferData() {
        const promises: PromiseLike<any>[] = [
            this.getVehicleData(),
            this.getZones(),
            this.getDiagnosticData(),
            this.getTemperatureData(),
            this.getMultiStateInputData()
        ]

        return Promise.all(promises);
    }

    private getVehicleData() {
        const vehiclesPromise = this.vehicleService.getVehicles({ includeLatestPlots: true }) as Promise<IVehicle[]>;
        const sensorDataPromise = this.sensorDataService.getLatestSensorDataValues();

        return Promise.all([vehiclesPromise, sensorDataPromise]).then(([vehicles, sensorDataValues]) => {
            this.vehicles = vehicles;
            this.sensorDataValues = sensorDataValues;
        })
    }

    private getZones() {
        return lastValueFrom(this.zoneService.getZones()).then(zones => {
            this.zones = zones;
        })
    }

    private getDiagnosticData() {
        const faultCodePromises: any[] = [
            this.faultCodeService.getActiveDiagnosticRecords(),
            this.faultCodeService.getReeferDiagnosticTroubleCodes()
        ]

        return Promise.all(faultCodePromises).then(([diagnosticRecords, troubleCodes]) => {
            this.diagnosticRecords = this.reeferService.filterReeferErrorCodes(diagnosticRecords);
            this.diagnosticTroubleCodes = troubleCodes;
        })
    }

    private getTemperatureData() {

        const temperaturePromises: any[] = [
            this.vehicleService.getLatestVehicleTemperatureRecords(false),
            lastValueFrom(this.temperatureSetpointService.getTemperatureSetpoints()),
            lastValueFrom(this.temperatureSetpointService.getTemperatureSetpointHistories({ Active: true })),
            lastValueFrom(this.temperatureProbeService.getTemperatureProbes()),
            lastValueFrom(this.zoneService.getZonesTemperatureOutOfBoundsHistory({ Active: true }))
        ];

        return Promise.all(temperaturePromises).then(([temperatureRecords, temperatureSetpoints, setpointHistories, temperatureProbes, outOfBoundsHistory]) => {
            this.temperatureRecords = temperatureRecords;
            this.temperatureSetpoints = temperatureSetpoints;
            this.temperatureSetpointHistories = setpointHistories;
            this.temperatureProbes = temperatureProbes;
            this.zoneTemperatureOutOfBoundsHistory = outOfBoundsHistory;
        })
    }

    private getMultiStateInputData() {
        const multiStateInputPromises: Promise<any>[] = [
            lastValueFrom(this.multiStateInputService.getLatestMultiStateInputRecords()),
            lastValueFrom(this.multiStateInputService.getMultiStateInputs())
        ];
        return Promise.all(multiStateInputPromises).then(([latestMultiStateInputRecords, multiStateInputs]) => {
            this.latestMultiStateInputRecords = latestMultiStateInputRecords;
            this.multiStateInputs = multiStateInputs;
        })
    }

    private mapPropertiesToZones(reeferUnit: IReeferWithMappings) {
        reeferUnit.zones = this.zones.filter(zone => zone.VehicleId === reeferUnit.VehicleId);

        if (!reeferUnit.zones || !reeferUnit.zones.length) {
            return;
        }

        const probes = this.temperatureProbes.filter(probe => probe.VehicleId === reeferUnit.VehicleId);
        const reeferZoneModes = this.zoneService.getLatestReeferZoneOperatingModes(this.latestMultiStateInputRecords, this.multiStateInputs, this.reeferService.getHardwareSource(reeferUnit.Make));

        this.zoneService.mapProbesToZone(reeferUnit.zones, probes);
        this.zoneService.mapZoneStatuses(reeferUnit.zones, reeferZoneModes);
        this.zoneService.mapTemperatureSetpointsToZones(reeferUnit.zones, this.temperatureSetpoints);
        this.zoneService.mapOutOfBoundsHistoryToZones(reeferUnit.zones, this.zoneTemperatureOutOfBoundsHistory);
        this.temperatureProbeService.mapProbesLatestRecord(reeferUnit.zones, this.temperatureRecords);
    }

    private setupSensorDataForReefer(reeferUnit: IReeferWithMappings) {
        const vehicle = this.vehicles.find(vehicle => vehicle.Id === reeferUnit.VehicleId) as IVehicleWithSensorDataValues;
        this.reeferService.setupSensorDataForReefer(reeferUnit, vehicle, this.sensorDataValues);
    }

    private getModes(reeferUnit: IReeferWithMappings) {
        const hardwareSource = this.reeferService.getHardwareSource(reeferUnit.Make);
        reeferUnit.operatingMode = this.latestMultiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferOperatingMode && record.Source == hardwareSource && record.VehicleId === reeferUnit.VehicleId)?.State;
        reeferUnit.engineMode = this.latestMultiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferEngineMode && record.Source == hardwareSource && record.VehicleId === reeferUnit.VehicleId)?.State;
    }

    private getActiveReeferError(reefer: IReeferWithMappings): { error: string, status: VTableRowStatus } {
        let error: string;
        let status: VTableRowStatus = VTableRowStatus.Info;

        if (reefer.activeErrors.length) {
            error = reefer.activeErrors[0]?.Description;
            status = VTableRowStatus.Error;
            return { error, status };
        }

        if (this.zoneService.hasZoneOutOfBounds(reefer)) {
            let outOfBoundsList = [];
            const zonesOutOfBounds = reefer.zones.filter(zone => zone.outOfBounds);
            zonesOutOfBounds.forEach(zone => {
                outOfBoundsList.push(zone.Position);
            })

            error = this.translationService.t("ReeferList.ZonesOutOfBounds", outOfBoundsList.join(', '));
            status = VTableRowStatus.Warning;
        }


        if (this.isReeferInactive(reefer)) {
            error = `${this.translationService.t("ReeferList.Active")} ${this.dateService.relativeTime(reefer.lastUpdated)}`;
            status = VTableRowStatus.Warning;
        }

        return { error, status };
    }

    private isReeferInactive(reefer: IReeferWithMappings) {
        return !this.dateService.date1IsAfterDate2(reefer.lastUpdated, this.dateRangeStart);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: (reeferUnit) => this.vTableService.createStatusCell(reeferUnit.errorStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReeferList.Alerts", width: 200, minTableWidth: 475 }),
                createCellFunction: (reeferUnit) => this.vTableService.createTableCell(reeferUnit.currentError)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (reeferUnit) => this.vTableService.createVehicleRegCell(reeferUnit.vehicle)
            },
            {
                header: this.vTableService.createLocationInformationHeader({ minTableWidth: 1500 }),
                createCellFunction: (reeferUnit) => this.vTableService.createLocationInformationCell({ plot: reeferUnit.vehicle.Plot })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReeferList.Fuel", minTableWidth: 1400 }),
                createCellFunction: (reeferUnit) => this.vTableService.createTableCell(reeferUnit.fuelLevel,
                    {
                        component: VProgressBar,
                        componentInputs: { percentage: reeferUnit.fuelLevel, palette: ProgressColourPalette.RAG }
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReeferList.ReeferStatus", minTableWidth: 1300, width: 130, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.createOperatingModeCell(reeferUnit)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReeferList.Engine", minTableWidth: 1500, width: 130, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.createEngineModeCell(reeferUnit)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ZoneNo", 1), width: 60, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.createZoneOpModeCell(reeferUnit, 1)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SetPointZone", 1), minTableWidth: 800, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.createSetpointCellForZone(reeferUnit, 1)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ReturnZone", 1), minTableWidth: 600, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 1, TemperatureProbeCategory.Return)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SupplyZone", 1), minTableWidth: 1050, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 1, TemperatureProbeCategory.Supply)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.EvaporatorZone", 1), minTableWidth: 1500, sortType: SortType.Number, canEdit: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 1, TemperatureProbeCategory.Evaporator)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ZoneNo", 2), width: 60, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createZoneOpModeCell(reeferUnit, 2)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SetPointZone", 2), minTableWidth: 800, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createSetpointCellForZone(reeferUnit, 2)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ReturnZone", 2), minTableWidth: 600, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 2, TemperatureProbeCategory.Return)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SupplyZone", 2), minTableWidth: 1050, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 2, TemperatureProbeCategory.Supply)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.EvaporatorZone", 2), minTableWidth: 1500, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 2, TemperatureProbeCategory.Evaporator)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ZoneNo", 3), width: 60, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createZoneOpModeCell(reeferUnit, 3)
            },
            {

                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SetPointZone", 3), minTableWidth: 800, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createSetpointCellForZone(reeferUnit, 3)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ReturnZone", 3), minTableWidth: 600, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 3, TemperatureProbeCategory.Return)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SupplyZone", 3), minTableWidth: 1050, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 3, TemperatureProbeCategory.Supply)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.EvaporatorZone", 3), minTableWidth: 1500, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 3, TemperatureProbeCategory.Evaporator)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ZoneNo", 4), width: 60, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createZoneOpModeCell(reeferUnit, 4)
            },
            {

                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SetPointZone", 4), minTableWidth: 800, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.createSetpointCellForZone(reeferUnit, 4)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.ReturnZone", 4), minTableWidth: 600, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 4, TemperatureProbeCategory.Return)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.SupplyZone", 4), minTableWidth: 1050, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 4, TemperatureProbeCategory.Supply)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("ReeferList.EvaporatorZone", 4), minTableWidth: 1500, sortType: SortType.Number, canEdit: true, hidden: true }),
                createCellFunction: (reeferUnit) => this.getTemperatureReadingCell(reeferUnit, 4, TemperatureProbeCategory.Evaporator)
            },
            {
                header: this.vTableService.createDateTableHeader({ minTableWidth: 1200 }),
                createCellFunction: (reeferUnit) => this.vTableService.createDateCell(reeferUnit.lastUpdated, { showRelativeTime: true, showErrorIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min) })
            }

        ]
    }

    private getTemperatureReadingCell(reeferUnit: IReeferWithMappings, zoneNumber: number, category: TemperatureProbeCategory): IVTableCell {
        const reading = this.getTemperatureReading(reeferUnit.zones, zoneNumber, category);
        return this.vTableService.createTableCell(
            reading,
            {
                component: TemperatureReadingComponent,
                componentInputs: { temperatureReading: reading },
                exportData: this.conversionService.smartConvert(reading, UnitConversionType.Temperature, { precision: 1 })
            })
    }

    private createZoneOpModeCell(reeferUnit: IReeferWithMappings, zonePosition: number): IVTableCell {
        const opMode = this.zoneService.getZoneAtPosition(reeferUnit.zones, zonePosition)?.operatingMode;
        return this.vTableService.createTableCell(
            opMode,
            {
                classes: "text-center",
                component: ZoneOperatingModeWidget,
                componentInputs: { category: opMode }
            })
    }

    private createOperatingModeCell(reeferUnit: IReeferWithMappings): IVTableCell {
        const operatingModeString = reeferUnit.operatingMode ? this.translationService.tEnum(ReeferOperatingModeCategory, 'ReeferOperatingModeCategory', reeferUnit.operatingMode) : '';
        return this.vTableService.createTableCell(
            operatingModeString,
            { sortData: reeferUnit.operatingMode || -1 })
    }

    private createEngineModeCell(reeferUnit: IReeferWithMappings): IVTableCell {
        if (!reeferUnit.engineMode && reeferUnit.engineMode !== 0) {
            return this.vTableService.createTableCell("");
        }

        const engineModeString = this.translationService.tEnum(ReeferEngineModeCategory, 'ReeferEngineModeCategory', reeferUnit.engineMode);
        return this.vTableService.createTableCell(engineModeString, { sortData: reeferUnit.engineMode || -1 })
    }

    private createSetpointCellForZone(reeferUnit: IReeferWithMappings, zonePosition: number): IVTableCell {
        const reading = this.zoneService.getZoneAtPosition(reeferUnit.zones, zonePosition)?.setpointReading;

        return this.vTableService.createTableCell(
            reading,
            {
                component: TemperatureReadingComponent,
                componentInputs: { temperatureReading: reading },
                sortData: reading,
                exportData: this.conversionService.smartConvert(reading, UnitConversionType.Temperature, { precision: 1 })
            })
    }

    private getTemperatureReading(zones: IZoneWithProbes[], zonePosition: number, category: TemperatureProbeCategory): number {
        const zone = this.zoneService.getZoneAtPosition(zones, zonePosition);
        if (!zone || !zone.probes.length) {
            return;
        }

        const probe = this.temperatureProbeService.getProbeByType(zone.probes, category);

        if (!probe || !probe.latestRecord) {
            return;
        }

        return probe.latestRecord.Temperature;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("ReeferList.All", (r) => true);
        this.createTabDefinition("ReeferList.InAlert", (r) => r.activeErrors?.length > 0);
        this.createTabDefinition("ReeferList.OutOfBounds", (r) => this.zoneService.hasZoneOutOfBounds(r));
        this.createTabDefinition("ReeferList.LowFuel", (r) => r.fuelLevel && r.fuelLevel < 15);
        this.createTabDefinition("ReeferList.Inactive", (r) => this.isReeferInactive(r));
    }

    private createTabDefinition(translationString: string, callback: (reeferUnit: IReeferWithMappings) => boolean) {

        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }
}
